import React, { useCallback, useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';
import isTouchDevice from 'is-touch-device';
import { Clickable } from '@moda/om';
import { LookCellFragment } from '../../../generated/types';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { useAdminMode } from '../../AdminMode/AdminModeProvider';
import { ResponsiveImage } from '../../ResponsiveImage';
import { LooksCarouselItemDetails } from '../LooksCarouselItemDetails';
import { LooksClothesHanger } from '../LooksClothesHanger';
import { useLooksDesktopModule } from '../LooksDesktopModule/useLooksDesktopModule';
import { Loading } from '../../Loading';

import './LooksCarouselItem.scss';

const AdminModeLookOverlay = loadable(() => import('../../AdminMode/AdminModeLookOverlay'), {
  fallback: <Loading />
});

interface Props {
  className?: string;
  look: LookCellFragment;
}

export const LooksCarouselItem: React.FC<Props> = ({ look }) => {
  const { primaryImageUrls, id: lookId } = look;
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { mode, isAnyLookSelected } = useLooksDesktopModule();

  const { enabled: adminModeEnabled } = useAdminMode();

  const {
    prevParams,
    queryParams,
    queryParams: { look: selectedLookId },
    addParams
  } = useUrlParams();

  const handleMouseEnter = useCallback(() => {
    if (isTouchDevice()) return;
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice()) return;
    setIsHovered(false);
  }, []);

  useEffect(() => {
    if (!queryParams.look && prevParams?.look === lookId) {
      ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }, [lookId, prevParams?.look, queryParams.look]);

  const isSelected = selectedLookId === lookId;
  const isHidden = selectedLookId !== undefined && selectedLookId !== lookId;

  if (!primaryImageUrls || !primaryImageUrls.src) return null;

  return (
    <div
      className={classNames('LooksCarouselItem', {
        'LooksCarouselItem--selected': isSelected,
        'LooksCarouselItem--hidden': isHidden,
        'LooksCarouselItem--hovered': isHovered && !isSelected
      })}
      ref={ref}
    >
      {adminModeEnabled && (
        <AdminModeLookOverlay lookId={look.id} imageSrc={look.primaryImageUrls?.src} />
      )}

      <Clickable
        className="LooksCarouselItem__image-wrapper"
        onClick={() => addParams({ look: lookId })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid="LooksCarouselItem__image-wrapper"
        aria-label={look.name}
      >
        <ResponsiveImage
          className={classNames({
            'LooksCarouselItem__image--small': mode === 'small' && !isAnyLookSelected,
            'LooksCarouselItem__image--large': mode === 'large' || isAnyLookSelected
          })}
          key={lookId}
          src={primaryImageUrls.src}
          maxHeight={600}
          maxWidth={400}
          alt={look.name}
        />

        {isHovered && !isSelected && look.variantIds && (
          <div className="LooksCarouselItem__overlay">
            <LooksClothesHanger>{look.variantIds.length}</LooksClothesHanger>
          </div>
        )}
      </Clickable>

      <LooksCarouselItemDetails className="LooksCarouselItem__details" lookId={lookId} />
    </div>
  );
};
